import React, { useCallback, useEffect, useState } from "react";

import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useShopifyCart } from "@app/scripts/react-hooks/shopify";
import { CartFreeShipping } from "@app/scripts/react-cart/components/CartFreeShipping";
import { CartItems } from "@app/scripts/react-cart/components/CartItems";
import { CartOrderSummary } from "@app/scripts/react-cart/components/CartOrderSummary";

import type { GiftProduct, LineItem, Banner, FreeShipping } from "@app/scripts/types";
import { CartGiftPopup } from "./CartGiftPopup";
import { CartRewards } from "./CartRewards";

export type Props = {
  heading?: string;
  freeShipping?: FreeShipping;
  giftProduct?: GiftProduct;
  shippingHeading?: string;
  shippingHeadingUnder?: string;
  youMayAlsoLikeHeading?: string;
  giftCarouselHeading?: string;
  giftPopupHeading?: string;
  lowStockThreshold: number;
  youMayAlsoLikeCollection?: number[];
  youMayAlsoLikeProductsToShow: number;
  giftsInCart: LineItem[];
  banner?: Banner;
};

export const CartDrawer: React.FC<Props> = ({
  heading,
  giftProduct,
  freeShipping,
  youMayAlsoLikeHeading,
  lowStockThreshold,
  youMayAlsoLikeCollection,
  youMayAlsoLikeProductsToShow,
  giftCarouselHeading,
  giftPopupHeading,
  shippingHeading,
  shippingHeadingUnder,
  banner,
}) => {
  const shopifyCart = useShopifyCart({ giftProduct });
  const { cart, fetchCart, goToCheckout, giftIds, gifts, giftsInCart, overGiftThreshold, error, setError } =
    shopifyCart;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const requiresSelection = gifts?.length > 1 || gifts?.some((gift) => gift.variants?.length ?? 0 > 1);
  const shouldDisplayPopup = giftsInCart?.length === 0 && requiresSelection && overGiftThreshold;

  const gotToCheckoutWithPopup = (e: any, toggleOpen?: () => void) => {
    if (sessionStorage.getItem("giftPopupClosed") !== "true" && shouldDisplayPopup) {
      setIsPopupOpen(true);
      toggleOpen ? toggleOpen() : null;
    } else goToCheckout(e);
  };

  useEffect(() => {
    window.addEventListener("cartAdd", fetchCart);
    fetchCart();

    return () => {
      window.removeEventListener("cartAdd", fetchCart);
    };
  }, []);

  return !closed ? (
    <>
      <Drawer
        drawerClassName="cart-drawer"
        heading={`${heading ?? "Your Bag"}`}
        secondaryHeading={`(${cart?.item_count} Item${cart && cart.item_count > 1 ? "s" : ""})`}
        customButton={Btn}
      >
        <CartContent
          shopifyCart={shopifyCart}
          goToCheckout={(e, toggleOpen) => gotToCheckoutWithPopup(e, toggleOpen)}
          freeShipping={freeShipping}
          lowStockThreshold={lowStockThreshold}
          youMayAlsoLikeHeading={youMayAlsoLikeHeading}
          youMayAlsoLikeCollection={youMayAlsoLikeCollection}
          youMayAlsoLikeProductsToShow={youMayAlsoLikeProductsToShow}
          giftCarouselHeading={giftCarouselHeading}
          shippingHeading={shippingHeading}
          shippingHeadingUnder={shippingHeadingUnder}
          banner={banner}
        />
      </Drawer>
      <CartGiftPopup
        isOpen={isPopupOpen}
        setIsOpen={setIsPopupOpen}
        giftsInCart={giftsInCart}
        gifts={gifts}
        giftIds={giftIds}
        overGiftThreshold={overGiftThreshold}
        goToCheckout={goToCheckout}
        giftPopupHeading={giftPopupHeading}
      />
    </>
  ) : null;
};

// TODO: Fix any to correct types
const CartContent = ({
  shopifyCart,
  goToCheckout,
  freeShipping,
  toggleOpen,
  lowStockThreshold,
  youMayAlsoLikeHeading,
  youMayAlsoLikeCollection,
  youMayAlsoLikeProductsToShow,
  giftCarouselHeading,
  shippingHeading,
  shippingHeadingUnder,
  banner,
}: any) => {
  const { cart, error, totalDiscount, cartTotal, cartBeforeDiscountPrice, qualifyingSubtotal } = shopifyCart as ReturnType<
    typeof useShopifyCart
  >;

  return (
    <div className="cart__inner">
      <CartFreeShipping
        cart={cart}
        freeShipping={freeShipping}
        shippingHeading={shippingHeading}
        shippingHeadingUnder={shippingHeadingUnder}
        qualifyingSubtotal={qualifyingSubtotal}
      />
      <CartRewards />

      <CartItems
        shopifyCart={shopifyCart}
        lowStockThreshold={lowStockThreshold}
        notifyLabel="Notify Me"
        youMayAlsoLikeHeading={youMayAlsoLikeHeading}
        youMayAlsoLikeCollection={youMayAlsoLikeCollection}
        youMayAlsoLikeProductsToShow={youMayAlsoLikeProductsToShow}
        giftCarouselHeading={giftCarouselHeading}
        banner={banner}
      />

      <CartOrderSummary
        totalDiscount={totalDiscount}
        cart={cart}
        goToCheckout={goToCheckout}
        cartTotal={cartTotal}
        toggleOpen={toggleOpen}
        cartBeforeDiscountPrice={cartBeforeDiscountPrice}
        error={error}
      />
    </div>
  );
};

const Btn = ({ toggleOpen, open }: {
  toggleOpen: () => void,
  open: boolean
}) => {
  const openOnAdd = useCallback(() => {
    if (!open) toggleOpen?.();
  }, [open, toggleOpen]);

  useEffect(() => {
    const cartButtons = document.querySelectorAll("[data-togglecart]");

    cartButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        toggleOpen?.();
      });
    });

    window.addEventListener("cartAdd", openOnAdd);

    return () => {
      window.removeEventListener("cartAdd", openOnAdd);
    };
  }, []);

  return null;
};
